import React, { createContext, useContext, useState, useCallback } from "react";
import { analytics } from "../utils/analytics";

export interface PlacesPrediction {
  place_id: string;
  description: string;
  main_text: string;
  secondary_text: string;
  types: string[];
}

export interface PlaceDetails {
  place_id: string;
  formatted_address: string;
  name: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  address_components: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
}

interface GooglePlacesContextType {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  getAddressPredictions: (input: string) => Promise<PlacesPrediction[]>;
  getPlaceDetails: (placeId: string) => Promise<PlaceDetails | null>;
  retry: () => void;
}

interface GooglePlacesProviderProps {
  children: React.ReactNode;
  apiKey?: string;
}

const GooglePlacesContext = createContext<GooglePlacesContextType | null>(null);

export const GooglePlacesProvider: React.FC<GooglePlacesProviderProps> = ({
  children,
  apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placesLib, setPlacesLib] = useState<google.maps.PlacesLibrary | null>(
    null
  );
  const [initialized, setInitialized] = useState(false);

  const loadGoogleMapsScript = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (typeof google !== "undefined" && google.maps && google.maps.places) {
        resolve();
        return;
      }

      // Check if script is already loading
      const existingScript = document.querySelector(
        'script[src*="maps.googleapis.com"]'
      );
      if (existingScript) {
        // Use passive event listeners to avoid warnings
        existingScript.addEventListener("load", () => resolve(), {
          passive: true,
        });
        existingScript.addEventListener(
          "error",
          () => reject(new Error("Failed to load Google Maps script")),
          { passive: true }
        );
        return;
      }

      if (!apiKey) {
        reject(new Error("Google Maps API key not provided"));
        return;
      }

      const script = document.createElement("script");
      // Add loading=async parameter for better performance and ensure places library is loaded
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps&loading=async`;
      script.async = true;
      script.defer = true;

      // Create a global callback for when Google Maps is ready
      (window as any).initGoogleMaps = () => {
        console.log("Google Maps script loaded via callback");
        resolve();
      };

      script.onload = () => {
        console.log("Google Maps script onload triggered");
        // Give a small delay to ensure libraries are fully loaded
        setTimeout(() => resolve(), 100);
      };
      script.onerror = () =>
        reject(new Error("Failed to load Google Maps script"));

      document.head.appendChild(script);
    });
  }, [apiKey]);

  const initializeServices = useCallback(async () => {
    console.log("Initializing Google Places services...");
    console.log("Environment check:", {
      NODE_ENV: process.env.NODE_ENV,
      hasApiKey: !!apiKey,
      apiKeyLength: apiKey?.length,
      apiKeyStart: apiKey ? apiKey.substring(0, 8) + "..." : "none",
    });

    if (!apiKey) {
      const errorMsg =
        "Google Maps API key not provided - check REACT_APP_GOOGLE_MAPS_API_KEY environment variable";
      console.error(errorMsg);
      setError(errorMsg);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log("Loading Google Maps script...");
      await loadGoogleMapsScript();

      // Wait for Google Maps to be fully available
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait

      while (attempts < maxAttempts) {
        if (typeof google !== "undefined" && google.maps) {
          console.log("Google Maps API detected, checking Places library...");

          // Check if Places library is loaded
          if (
            google.maps.places ||
            typeof google.maps.importLibrary === "function"
          ) {
            break;
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (typeof google !== "undefined" && google.maps) {
        console.log(
          "Google Maps loaded, checking importLibrary availability..."
        );

        // Check if importLibrary is available (requires newer version of Google Maps API)
        let loadedLib: google.maps.PlacesLibrary | null = null;
        if (typeof google.maps.importLibrary === "function") {
          console.log("Using new importLibrary method...");
          // Use the new importLibrary method for Places API
          const placesLibrary = (await google.maps.importLibrary(
            "places"
          )) as google.maps.PlacesLibrary;

          console.log("Places library imported successfully");
          setPlacesLib(placesLibrary);
          loadedLib = placesLibrary;
        } else {
          console.log("importLibrary not available, using legacy method...");
          // Fallback to legacy method if importLibrary is not available
          if (google.maps.places) {
            console.log("Using legacy google.maps.places");
            setPlacesLib(google.maps.places as any);
            loadedLib = google.maps.places as any;
          } else {
            throw new Error(
              "Google Maps Places library not available after loading"
            );
          }
        }

        setIsLoaded(true);
        analytics.track("google_places_initialized", "places_service");
        return loadedLib;
      } else {
        console.error("Google Maps API not properly loaded after waiting");
        throw new Error("Google Maps API not properly loaded");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      console.error("Error initializing Google Places services:", errorMessage);
      setError(errorMessage);
      analytics.track("google_places_init_error", "places_service", {
        error: errorMessage,
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, loadGoogleMapsScript]);

  // Returns the Places library directly rather than relying on the `placesLib`
  // state var, which callers awaiting this can't see updated within their own
  // already-running closure (state updates don't retroactively change a
  // function invocation's captured variables) — this was causing the very
  // first address search of a session to always return 0 results even though
  // initialization completed successfully moments earlier.
  const lazyInitialize = useCallback(async (): Promise<google.maps.PlacesLibrary | null> => {
    if (!initialized && !isLoading) {
      setInitialized(true);
      return await initializeServices();
    }
    return placesLib;
  }, [initialized, isLoading, initializeServices, placesLib]);

  const getAddressPredictions = useCallback(
    async (input: string): Promise<PlacesPrediction[]> => {
      // Lazy initialize when first used — use the returned library directly
      // instead of the (possibly still-stale) `placesLib` state var, since this
      // call's closure won't see a state update that happens during its own await.
      let effectiveLib = placesLib;
      if (!isLoaded && !isLoading) {
        effectiveLib = await lazyInitialize();
      }

      // If input is empty, don't make API call but still trigger initialization
      if (!input.trim()) {
        return [];
      }

      if (!effectiveLib) {
        console.warn("Places library not available");
        return [];
      }

      try {
        // Use the modern AutocompleteService from the library
        let AutocompleteService;

        if (effectiveLib.AutocompleteService) {
          // New library structure
          AutocompleteService = effectiveLib.AutocompleteService;
        } else if (
          google.maps.places &&
          google.maps.places.AutocompleteService
        ) {
          // Legacy structure
          AutocompleteService = google.maps.places.AutocompleteService;
        } else {
          throw new Error("AutocompleteService not available");
        }

        const service = new AutocompleteService();

        return new Promise((resolve) => {
          const request: google.maps.places.AutocompletionRequest = {
            input,
            types: ["address"],
            componentRestrictions: { country: "au" },
            // Bias results to Victoria, Australia (centered on Melbourne)
            location: new google.maps.LatLng(-37.8136, 144.9631),
            radius: 200000, // 200km radius from Melbourne to cover most of Victoria
          };

          service.getPlacePredictions(
            request,
            (
              predictions: google.maps.places.AutocompletePrediction[] | null,
              status: google.maps.places.PlacesServiceStatus
            ) => {
              if (
                status === google.maps.places.PlacesServiceStatus.OK &&
                predictions
              ) {
                const formattedPredictions: PlacesPrediction[] =
                  predictions
                    .map(
                      (
                        prediction: google.maps.places.AutocompletePrediction
                      ) => ({
                        place_id: prediction.place_id!,
                        description: prediction.description,
                        main_text:
                          prediction.structured_formatting?.main_text ||
                          prediction.description,
                        secondary_text:
                          prediction.structured_formatting?.secondary_text || "",
                        types: prediction.types,
                      })
                    )
                    .filter((prediction) => {
                      // Filter to only include Victorian addresses
                      const description = prediction.description.toLowerCase();
                      const secondaryText = prediction.secondary_text.toLowerCase();
                      
                      // Check if the address contains Victoria indicators
                      const isVictoria = 
                        description.includes(' vic ') || 
                        description.includes(' vic,') || 
                        description.endsWith(' vic') ||
                        secondaryText.includes(' vic ') || 
                        secondaryText.includes(' vic,') || 
                        secondaryText.endsWith(' vic') ||
                        description.includes(' victoria ') || 
                        description.includes(' victoria,') || 
                        description.endsWith(' victoria') ||
                        secondaryText.includes(' victoria ') || 
                        secondaryText.includes(' victoria,') || 
                        secondaryText.endsWith(' victoria');
                      
                      return isVictoria;
                    });

                analytics.track(
                  "google_places_predictions_success",
                  "places_service",
                  {
                    input,
                    resultsCount: formattedPredictions.length,
                  }
                );

                resolve(formattedPredictions);
              } else {
                analytics.track(
                  "google_places_predictions_error",
                  "places_service",
                  {
                    input,
                    status: status.toString(),
                  }
                );
                resolve([]);
              }
            }
          );
        });
      } catch (error) {
        console.error("Failed to get address predictions:", error);
        analytics.track("google_places_predictions_error", "places_service", {
          input,
          error: error instanceof Error ? error.message : "Unknown error",
        });
        return [];
      }
    },
    [placesLib, lazyInitialize, isLoaded, isLoading]
  );

  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<PlaceDetails | null> => {
      // Lazy initialize when first used — see getAddressPredictions for why we
      // use the returned library rather than the `placesLib` state var here.
      let effectiveLib = placesLib;
      if (!isLoaded && !isLoading) {
        effectiveLib = await lazyInitialize();
      }

      if (!effectiveLib) {
        console.warn("Places library not available");
        return null;
      }

      try {
        // Use the modern PlacesService from the library
        let PlacesService;

        if (effectiveLib.PlacesService) {
          // New library structure
          PlacesService = effectiveLib.PlacesService;
        } else if (google.maps.places && google.maps.places.PlacesService) {
          // Legacy structure
          PlacesService = google.maps.places.PlacesService;
        } else {
          throw new Error("PlacesService not available");
        }

        // Create a div element for PlacesService (still required by Google)
        const div = document.createElement("div");
        const map = new google.maps.Map(div);
        const service = new PlacesService(map);

        return new Promise((resolve) => {
          const request: google.maps.places.PlaceDetailsRequest = {
            placeId,
            fields: [
              "place_id",
              "formatted_address",
              "name",
              "geometry.location",
              "address_components",
            ],
          };

          service.getDetails(
            request,
            (
              place: google.maps.places.PlaceResult | null,
              status: google.maps.places.PlacesServiceStatus
            ) => {
              if (
                status === google.maps.places.PlacesServiceStatus.OK &&
                place
              ) {
                const details: PlaceDetails = {
                  place_id: place.place_id!,
                  formatted_address: place.formatted_address!,
                  name: place.name!,
                  geometry: {
                    location: {
                      lat: place.geometry!.location!.lat(),
                      lng: place.geometry!.location!.lng(),
                    },
                  },
                  address_components:
                    place.address_components?.map(
                      (component: google.maps.GeocoderAddressComponent) => ({
                        long_name: component.long_name,
                        short_name: component.short_name,
                        types: component.types,
                      })
                    ) || [],
                };

                analytics.track(
                  "google_places_details_success",
                  "places_service",
                  { placeId }
                );
                resolve(details);
              } else {
                analytics.track(
                  "google_places_details_error",
                  "places_service",
                  {
                    placeId,
                    status: status.toString(),
                  }
                );
                resolve(null);
              }
            }
          );
        });
      } catch (error) {
        console.error("Failed to get place details:", error);
        analytics.track("google_places_details_error", "places_service", {
          placeId,
          error: error instanceof Error ? error.message : "Unknown error",
        });
        return null;
      }
    },
    [placesLib, lazyInitialize, isLoaded, isLoading]
  );

  const retry = useCallback(() => {
    setInitialized(false);
    lazyInitialize();
  }, [lazyInitialize]);

  // Remove automatic initialization - now only loads when needed

  const contextValue: GooglePlacesContextType = {
    isLoaded,
    isLoading,
    error,
    getAddressPredictions,
    getPlaceDetails,
    retry,
  };

  return (
    <GooglePlacesContext.Provider value={contextValue}>
      {children}
    </GooglePlacesContext.Provider>
  );
};

export const useGooglePlaces = (): GooglePlacesContextType => {
  const context = useContext(GooglePlacesContext);
  if (!context) {
    throw new Error(
      "useGooglePlaces must be used within a GooglePlacesProvider"
    );
  }
  return context;
};
